import {
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ButtonZoom } from "../v2/utils/ButtonZoom";
import { ReactComponent as ArrowIcon } from "../../assets/ArrowRightWhiteIcon.svg";
import { Question } from "../utils/CourseTree";
import { KeyboardArrowUpRounded } from "@mui/icons-material";
import { useState } from "react";
import { CourseMetadata } from "@backend-types";
import { styled } from "@mui/material/styles";
import { generateGtmEvent } from "../../Helpers";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: 32,
    paddingRight: 32,
    [theme.breakpoints.up("lg")]: {
      paddingLeft: 86,
      paddingRight: 86,
    },
    overflow: "hidden",
    transition: "height 0.2s ease-in-out",
    flexShrink: 0,
  },
  opened: {
    height: "77px",
  },
  closed: {
    height: "0px",
  },
  default: {
    backgroundColor: "#FFFFFF",
    borderTop: `2px solid ${theme.palette.divider}`,
  },
  barButton: {
    height: "52px",
    borderRadius: "12px",
    padding: "0px 16px",
  },
  barButtonBasic: {
    color: "#747474",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "20px",
    background: "transparent",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.04)",
    },
    "&:focus": {
      background: "rgba(0, 0, 0, 0.04)",
    },
    "&:active": {
      background: "rgba(0, 0, 0, 0.08)",
    },
  },
  arrow: {
    marginLeft: "12px",
    width: "18px",
    height: "21px",
  },
  feedback: {
    color: "#373737",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "normal",
    letterSpacing: "0px",
  },
  checkCircle: {
    display: "flex",
    width: "32px",
    height: "32px",
    flexDirection: "column",
    justifyContent: "center",
    flexShrink: "0",

    textAlign: "center",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: "900",
    lineHeight: "normal",
  },
}));

interface Lti1p3BotNavProps {
  open?: boolean;
  remediationOpen?: boolean;
  continueButtonText?: string;
  showCheckWork?: boolean;
  showRetry?: boolean;
  showTrySimilar?: boolean;
  showPracticeMore?: boolean;
  feedback: BarMessageProps | null;
  showModuleHome?: boolean;
  handleModuleHome: () => void;
  handleMoveForward: (
    goToProblem: Question | null,
    suppressEoT?: boolean,
    suppressEoM?: boolean
  ) => void;
  handleDismissRemediation: () => void;
  handleClickRemediation: () => void;
  handleClickCheckWork: () => void;
  handleClickRetry: () => void;
  handleClickTrySimilar: () => void;
  handleNavigateAway: (callBack: () => void) => void;
  metadata?: CourseMetadata;
}

export const Lti1p3BotNav = (props: Lti1p3BotNavProps) => {
  const {
    continueButtonText = "Continue",
    open = false,
    remediationOpen = false,
    handleMoveForward,
    showModuleHome,
    handleModuleHome,
    handleDismissRemediation,
    handleClickRemediation,
    feedback,
    showPracticeMore,
  } = props;
  const classes = useStyles();

  const showBar = open || remediationOpen;

  return (
    <Box
      className={`${classes.root} ${classes.default} ${
        showBar ? classes.opened : classes.closed
      }`}>
      <Stack direction="row" alignItems="center" sx={{ height: "100%" }}>
        {open && !remediationOpen && (
          <>
            {feedback ? <BarMessage {...feedback} /> : <Box sx={{ flexGrow: 1 }}></Box>}

            <Box sx={{ flexShrink: 0 }}>
              {showPracticeMore && <PracticeMoreButton {...props} />}
              {/* CHM go home button */}
              {showModuleHome && (
                <ButtonZoom
                  sx={{ marginLeft: "18px" }}
                  tabIndex={5}
                  onClick={() => {
                    props.handleNavigateAway(() => handleModuleHome());
                    generateGtmEvent({
                      event: "link",
                      action: "click",
                      name: "onclick",
                      type: "internal link",
                      region: "footer",
                      section: "primary footer",
                      text: "Module home",
                    });
                  }}
                  className={classes.barButton}
                  variant="outlined"
                  color="primary"
                  size="large">
                  Module home
                </ButtonZoom>
              )}
              {/* move forward */}
              <ButtonZoom
                sx={{ marginLeft: "18px" }}
                tabIndex={5}
                onClick={() => {
                  props.handleNavigateAway(() => handleMoveForward(null, false, false));
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "footer",
                    section: "primary footer",
                    text: continueButtonText.toLowerCase(),
                  });
                }}
                className={classes.barButton}
                variant="contained"
                color="primary"
                size="large">
                {continueButtonText}
                <SvgIcon className={classes.arrow}>
                  <ArrowIcon />
                </SvgIcon>
              </ButtonZoom>
            </Box>
          </>
        )}
        {/* Remediation Message */}
        {remediationOpen && (
          <>
            <BarMessage
              icon={<i className="fa-solid fa-lightbulb"></i>}
              iconColor="#FE8B06"
              message="It looks like you’re still struggling. Would you like some extra help?"
            />

            <Box sx={{ flexShrink: 0 }}>
              <Button
                className={`${classes.barButton} ${classes.barButtonBasic}`}
                onClick={() => {
                  handleDismissRemediation();
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "footer",
                    section:
                      "it looks like you're still struggling. would you like some extra help?",
                    text: "no, thanks",
                  });
                }}>
                No, thanks
              </Button>
              <ButtonZoom
                sx={{ marginLeft: "18px" }}
                tabIndex={5}
                className={classes.barButton}
                variant="contained"
                color="primary"
                size="large"
                onClick={() => {
                  handleClickRemediation();
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "footer",
                    section:
                      "it looks like you're still struggling. would you like some extra help?",
                    text: "yes, please",
                  });
                }}>
                Yes, please
              </ButtonZoom>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
};

export interface BarMessageProps {
  message: string;
  iconColor: string;
  icon: JSX.Element;
}

export const BarMessage = ({ icon, iconColor, message }: BarMessageProps) => {
  const classes = useStyles();

  return (
    <Stack direction="row" alignItems="center" spacing="8px" sx={{ flexGrow: 1 }}>
      <Box className={classes.checkCircle} sx={{ color: iconColor }}>
        {icon}
      </Box>
      <Typography variant="h5" component="span" className={classes.feedback}>
        {message}
      </Typography>
    </Stack>
  );
};

const usePracticeButtonStyles = makeStyles((theme) => ({
  button: {
    borderRadius: "12px",
    height: "52px",
    padding: "0px 16px",
    color: "#747474",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "20px",
    overflow: "visible",
  },
  buttonClosed: {
    background: "transparent",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.04)",
    },
    "&:focus": {
      background: "rgba(0, 0, 0, 0.04)",
    },
    "&:active": {
      background: "rgba(0, 0, 0, 0.08)",
    },
  },
  buttonOpened: {
    background: "rgba(0, 0, 0, 0.08)",
  },
  arrowDown: {
    height: "fit-content",
    transform: "rotate(0deg)",
    transition: "transform .2s linear",
  },
  arrowUp: {
    height: "fit-content",
    transform: "rotate(180deg)",
    transition: "transform .2s linear",
  },
  menuPaper: {
    overflow: "visible",
    backgroundColor: "transparent",
  },
  menuList: {
    transform: "translate(0px, -8px)",
    backgroundColor: "#FFF",
    boxShadow: "0px 8px 12px 0px rgba(0, 0, 0, 0.19)",
    width: "259px",
  },
  menuWrapper: {
    fontFamily: "Roboto, sans-serif",
    padding: "0 0.938rem",
  },
  menuName: {
    fontSize: 20,
    fontWeight: 700,
    padding: "0.25rem 1rem",
    margin: 0,
  },
  menuItem: {
    padding: "8px 12px",
  },
  menuItemIcon: {
    marginRight: "8px",

    fontSize: "1em",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "normal",
  },
}));

const NoMaxWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
  },
});

export const PracticeMoreButton = (props: Lti1p3BotNavProps) => {
  const classes = usePracticeButtonStyles();
  const [practiceMoreOpen, setPracticeMoreOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [checkWorkHover, setCheckWorkHover] = useState(false);
  const [restartHover, setRestartHover] = useState(false);
  const [trySimilarHover, setTrySimilarHover] = useState(false);

  const handleClick = (e: any) => {
    setPracticeMoreOpen(true);
    // HandleDataLayer("collapse", "open", "navbar", "main navbar", "profile dropdown");
    setAnchorEl(e.currentTarget);
    generateGtmEvent({
      event: "collapse",
      action: "open",
      name: "onclick",
      type: "click",
      region: "footer",
      section: "primary footer",
      text: "practice more",
    });
  };
  const handleClose = () => {
    setPracticeMoreOpen(false);
    generateGtmEvent({
      event: "collapse",
      action: "close",
      name: "onclick",
      type: "click",
      region: "footer",
      section: "primary footer",
      text: "practice more",
    });
    setAnchorEl(null);
  };

  const buttonDisabled =
    !props.showCheckWork && !props.showRetry && !props.showTrySimilar;

  return (
    <>
      <NoMaxWidthTooltip
        title={
          buttonDisabled
            ? "Mastery achieved! For more practice return to a practice problem."
            : ""
        }
        placement="top"
        arrow>
        <span>
          <Button
            disabled={buttonDisabled}
            tabIndex={5}
            onClick={handleClick}
            className={`${classes.button} ${
              practiceMoreOpen ? classes.buttonOpened : classes.buttonClosed
            }`}
            disableRipple
            variant="contained"
            color="primary"
            size="medium">
            Practice more
            <KeyboardArrowUpRounded
              className={practiceMoreOpen ? classes.arrowDown : classes.arrowUp}
            />
          </Button>
        </span>
      </NoMaxWidthTooltip>
      <Menu
        open={practiceMoreOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        classes={{
          root: classes.menuWrapper,
          paper: classes.menuPaper,
          list: classes.menuList,
        }}
        PaperProps={{
          elevation: 0,
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}>
        {props.showCheckWork && (
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              handleClose();
              props.handleClickCheckWork();
              generateGtmEvent({
                event: "link",
                action: "click",
                name: "onclick",
                type: "internal link",
                region: "main content",
                section: "practice more",
                text: "check work",
              });
            }}
            onMouseEnter={() => setCheckWorkHover(true)}
            onMouseLeave={() => setCheckWorkHover(false)}
            style={{}}>
            <i
              className={`${checkWorkHover ? "fa-solid" : "fa-regular"} fa-pencil ${
                classes.menuItemIcon
              }`}></i>
            Check work
          </MenuItem>
        )}
        {props.showRetry && (
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              handleClose();
              props.handleClickRetry();
              generateGtmEvent({
                event: "link",
                action: "click",
                name: "onclick",
                type: "internal link",
                region: "main content",
                section: "practice more",
                text: "restart problem",
              });
            }}
            onMouseEnter={() => setRestartHover(true)}
            onMouseLeave={() => setRestartHover(false)}
            style={{}}>
            <i
              className={`${
                restartHover ? "fa-solid" : "fa-regular"
              } fa-arrow-rotate-left ${classes.menuItemIcon}`}></i>
            Restart problem
          </MenuItem>
        )}
        {props.showTrySimilar && (
          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              handleClose();
              props.handleClickTrySimilar();
              generateGtmEvent({
                event: "link",
                action: "click",
                name: "onclick",
                type: "internal link",
                region: "main content",
                section: "practice more",
                text: "try similar problem",
              });
            }}
            onMouseEnter={() => setTrySimilarHover(true)}
            onMouseLeave={() => setTrySimilarHover(false)}
            style={{}}>
            <i
              className={`${trySimilarHover ? "fa-solid" : "fa-regular"} fa-dice ${
                classes.menuItemIcon
              }`}></i>
            Try similar problem
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
